import React, { useEffect, useState } from 'react';
import { GetRequest, Tooltip, ValidatorFactory } from 'components/common';

// Numeric point count from a "point_N" value (e.g. "point_4" -> 4).
const pointNumber = (value) => {
  const number = parseInt(String(value).replace('point_', ''), 10);
  return Number.isNaN(number) ? null : number;
};

// Glueing point-count select for a unit price. Offers the standard point counts, but only up to the
// point count of the machine selected elsewhere on the price form (config.source_attribute_field): a
// machine that glues at most 4 points cannot be priced for 6. The machine's point count is read from its
// UserUnitPriceAttribute properties, so the list re-filters whenever a different machine is chosen.
const GlueingPointCountSelectFormField = ({ config, value, values, setValue, register, errors, disabled }) => {
  const [machineProperties, setMachineProperties] = useState(null);

  const machineAttributeId = values[config.source_attribute_field];

  // Load the selected machine's properties (its max point count) whenever the selection changes. Not
  // cached: a machine's point count can be edited within the same session.
  useEffect(() => {
    if (!/^\d+$/.test(String(machineAttributeId))) {
      setMachineProperties(null);
      return;
    }

    GetRequest({
      url: `/quote-builder/user_unit_price_attributes/properties/${machineAttributeId}`,
      callback: (data) => { setMachineProperties(data); }
    });
  }, [machineAttributeId]);

  const machineMaxPoint = machineProperties ? pointNumber(machineProperties.glueing_point_count) : null;
  const availableOptions = machineMaxPoint == null
    ? config.values
    : config.values.filter((option) => {
      const optionPoint = pointNumber(option.value);
      return optionPoint != null && optionPoint <= machineMaxPoint;
    });

  // Drop a previously-chosen point count the newly-selected machine cannot do (e.g. switching from a
  // 6-point machine to a 4-point one while "6 Point" was selected).
  useEffect(() => {
    if (value && machineMaxPoint != null && !availableOptions.some((option) => option.value === value)) {
      setValue(config.name, '');
    }
  }, [value, machineMaxPoint, availableOptions.length]);

  const onChange = (e) => {
    setValue(config.name, e.target.value);
  };

  // Run configured client-side validators (e.g. NonEmptyValidator) as an inline field error.
  const validate = (val) => {
    if (config.validators != undefined) {
      for (let i = 0; i < config.validators.length; i++) {
        let validator = config.validators[i];
        if (!ValidatorFactory(validator, val)) {
          return t(`quote_builder.validator_${validator}_message`);
        }
      }
    }
  };

  return (
    <tr>
      <td>
        {t(config.label)} {config.tooltip_text != undefined && <Tooltip title={t(config.tooltip_text)} />}
      </td>
      <td align="right">
        <select
          value={value || ''}
          name={config.name}
          disabled={disabled}
          className={`${errors[config.name] ? 'input-error form-control general-frm-element' : 'form-control general-frm-element'}`}
          {...register(config.name, { validate: validate, onChange: onChange })}
        >
          {availableOptions.map((option) => {
            return <option key={option.value} value={option.value}>{t(option.label)}</option>;
          })}
        </select>
        {errors[config.name] && <small className="error"> {errors[config.name].message} </small>}
      </td>
    </tr>
  );
};

export default GlueingPointCountSelectFormField;
